/**
 * **Retention** (`/settings/retention`, FR-25, FR-36; W28, FR-65, plan D86) —
 * how long the organisation keeps its documents, changed on the page and saved
 * by the bar:
 *
 * - **The organisation's rules** — a type kept longer than the law asks, for
 *   whichever app brought it or for one app's documents. Each rule carries its
 *   type's floor and citation beside it (D22). A rule below its floor is the
 *   service's `below_floor`, said **at the rule it names** (`refusedRow`); the
 *   bar says there is one. **Only the organisation's own** (`orgRetentionRules`):
 *   the service ships a rule at its floor for every type, which is the law's
 *   minimum, not a rule to edit or remove — removing one of the organisation's
 *   puts its type back to that floor.
 * - **Kept now** — each type in use with what the service applies today: the
 *   organisation's rule where it has one, else the law's minimum, the floor and
 *   its citation always beside it. What is saved, never the draft.
 *
 * The floor table rides on the settings answer (W6); an answer without it has
 * the table read from its own route, and a failure there is said — the service
 * still applies every floor to every document and every rule.
 */

import { SETTINGS, belowFloorOf, floorsByType, useLoad, type RetentionFloor, type RetentionRule, type SettingsAnswer, type TypeFloor } from '@fiveox/prodoc-ui'
import { Alert, Button, EmptyState, Input, Select, useToast } from '@fiveox/ui'
import { Archive, Hourglass, Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { SettingsSection } from '../../components/settings-parts'
import { useSettingsHost } from './host'
import { AreaWaiting, SETTINGS_BACK, orgRetentionRules, useSettingsArea } from './settings-data'

/** "8 years", "18 months" — a period said the way the statute says it. */
export function monthsText(months: number): string {
  if (months % 12 === 0) return `${months / 12} year${months === 12 ? '' : 's'}`
  return `${months} months`
}

/** When the counting starts: the end of the financial year that holds the document's date, or the upload. */
function anchorText(floor: TypeFloor | undefined): string {
  const anchors = new Set((floor?.floors ?? []).map((f) => f.anchor))
  return anchors.has('upload') && anchors.size === 1 ? 'after upload' : 'after the financial year ends'
}

/** "Statute: at least 8 years · Companies Act 2013 §128(5)" — the floor and the law that sets it. */
const statuteText = (floor: TypeFloor) => `Statute: at least ${monthsText(floor.months)} · ${floor.floors.map((f) => f.citation || f.label).join('; ')}`

/** One rule as the page edits it: the months as typed, and a key that outlives a removal above it. */
interface RuleRow {
  key: number
  document_type: string
  owner_app: string | null
  months: string
}

/* The page's slice of the settings document, and the one key it sends — module-level, so the area's memo holds. */
const rowsOf = (s: SettingsAnswer): RuleRow[] =>
  orgRetentionRules(s).map((r, i) => ({ key: i, document_type: r.document_type, owner_app: r.owner_app, months: String(r.months) }))
const rulesOf = (rows: RuleRow[]): RetentionRule[] => rows.map((r) => ({ document_type: r.document_type, owner_app: r.owner_app, months: Number(r.months) }))
const retentionPatch = (rows: RuleRow[]) => ({ retention: { rules: rulesOf(rows) } })
const sameRules = (a: RuleRow[], b: RuleRow[]) => JSON.stringify(rulesOf(a)) === JSON.stringify(rulesOf(b))

/**
 * Which submitted rule a `below_floor` refusal is about: its index when the
 * service gives one, else the rule of its type and app (`app/domain/retention.py`
 * names `document_type` and `owner_app`), else the first of its type.
 */
function refusedRow(rows: RuleRow[], refusal: unknown): number | null {
  const below = belowFloorOf(refusal)
  if (!below) return null
  if (below.index !== null && below.index >= 0 && below.index < rows.length) return below.index
  const exact = rows.findIndex((r) => r.document_type === below.document_type && (r.owner_app ?? null) === below.owner_app)
  if (exact >= 0) return exact
  const i = rows.findIndex((r) => r.document_type === below.document_type)
  return i >= 0 ? i : null
}

export function RetentionSettings() {
  const { client, can } = useSettingsHost()
  const { toast } = useToast()
  const area = useSettingsArea({ from: rowsOf, toPatch: retentionPatch, same: sameRules })
  const guard = useLeaveGuard(area.dirty)
  const editable = can(SETTINGS, 'can_update')
  const s = area.settings.data
  const rows = area.draft ?? []

  // The floor table rides on the settings answer from W6; its own route is read only when an answer lacks it.
  const lacking = Boolean(s) && !s?.floors?.length
  const floorTable = useLoad(async () => (lacking ? { asked: true, floors: await client.settings.floors() } : { asked: false, floors: [] as RetentionFloor[] }), [client, lacking])
  const tableFloors = floorTable.data?.floors
  const floors = useMemo(() => (s?.floors?.length ? s.floors : (tableFloors ?? [])), [s, tableFloors])
  const byType = useMemo(() => floorsByType(floors), [floors])
  const floorsUnread = floors.length === 0 && (Boolean(floorTable.error) || Boolean(floorTable.data?.asked))
  const options = useLoad(() => client.documents.options(), [client])

  const types = s?.effective.document_types ?? []
  const below = belowFloorOf(area.refusal)
  const refused = refusedRow(rows, area.refusal)
  const set = (i: number, next: Partial<RuleRow>) => area.setDraft(rows.map((r, j) => (j === i ? { ...r, ...next } : r)))

  /** What the page itself can say is wrong with a rule, before anything is sent. */
  const localError = (i: number): string | null => {
    const r = rows[i]!
    const n = Number(r.months)
    if (!r.months || !Number.isInteger(n) || n < 1 || n > 1200) return 'A whole number of months, 1 to 1,200.'
    if (rows.some((o, j) => j < i && o.document_type === r.document_type && o.owner_app === r.owner_app)) return 'A rule for this type and app is already above.'
    return null
  }
  const monthsError = (i: number): string | null => {
    const local = localError(i)
    if (local) return local
    // The service's refusal, at the rule it names — until the rule is lengthened to the floor it gave.
    if (refused !== i || !below) return null
    const months = below.months ?? byType.get(rows[i]!.document_type)?.months ?? null
    if (below.months !== null && Number(rows[i]!.months) >= below.months) return null
    return `Below the law’s minimum${months ? ` of ${monthsText(months)}` : ''}${below.citation ? ` (${below.citation})` : ''}.`
  }
  const invalid = rows.some((_, i) => localError(i) !== null)

  // A new rule starts where there is none yet: a type in use without a rule of its own, else such a type for one
  // app; only when every pair has one is it a repeat of the first type, marked until it is changed. It starts a
  // year above the floor — a rule at the floor is the law's minimum, which the type has already.
  const addRule = () => {
    const free = (type: string, app: string | null) => !rows.some((r) => r.document_type === type && (r.owner_app ?? null) === app)
    const inUse = types.filter((t) => t.enabled)
    const apps = options.data?.owner_apps ?? []
    const pick =
      inUse.map((t) => ({ type: t.code, app: null as string | null })).find((c) => free(c.type, c.app)) ??
      inUse.flatMap((t) => apps.map((app) => ({ type: t.code, app }))).find((c) => free(c.type, c.app)) ??
      (types[0] ? { type: types[0].code, app: null } : null)
    if (!pick) return
    const key = Math.max(-1, ...rows.map((r) => r.key)) + 1
    area.setDraft([...rows, { key, document_type: pick.type, owner_app: pick.app, months: String((byType.get(pick.type)?.months ?? 12) + 12) }])
  }

  const save = () => void area.save().then((saved) => saved && toast('Settings saved.'))

  return (
    <EditPage back={SETTINGS_BACK} title="Retention" description="How long documents are kept. The law sets a minimum for each type; the organisation may keep them longer, never shorter.">
      {guard}
      {!s || !area.draft ? (
        <AreaWaiting settings={area.settings} label="Loading the retention settings" heights={['h-56', 'h-72']} />
      ) : (
        <>
          {floorsUnread && (
            <Alert tone="warning">
              The statute’s minimums could not be read{floorTable.error ? ` (${floorTable.error})` : ''}. The service still applies them to every document and every rule.
            </Alert>
          )}

          <SettingsSection
            id="rules"
            icon={<Hourglass size={18} aria-hidden />}
            title="The organisation’s rules"
            description="Keep a type longer than the law asks — whichever app brought it, or one app’s documents. A rule may only lengthen the law’s minimum; the service refuses a shorter one."
          >
            <p className="text-muted-foreground mb-4 text-sm">
              Months count from the end of the financial year that holds the document’s date (or its upload, when it has none) — for Contract and Other, from the
              upload.
            </p>
            {rows.length === 0 ? (
              <EmptyState
                variant="outline"
                title="No rules of the organisation’s own yet"
                description={`Every type is kept for the law’s minimum, and a type the law names none for, a year after upload.${editable ? ' Add a rule to keep a type longer.' : ''}`}
              />
            ) : (
              <ul className="space-y-3" aria-label="Retention rules">
                {rows.map((r, i) => {
                  const floor = byType.get(r.document_type)
                  return (
                    <li key={r.key} data-rule={i} className="border-border grid gap-3 rounded-xl border p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_10rem_auto] sm:items-start">
                      <Select label="Type" value={r.document_type} disabled={!editable} onChange={(e) => set(i, { document_type: e.target.value })}>
                        {types.map((t) => (
                          <option key={t.code} value={t.code}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                      <Select label="Brought by" value={r.owner_app ?? ''} disabled={!editable} onChange={(e) => set(i, { owner_app: e.target.value || null })}>
                        <option value="">Any app</option>
                        {[...new Set([...(options.data?.owner_apps ?? []), ...(r.owner_app ? [r.owner_app] : [])])].map((app) => (
                          <option key={app} value={app}>
                            {app}
                          </option>
                        ))}
                      </Select>
                      <Input type="number" min={1} max={1200} label="Months" value={r.months} disabled={!editable} onChange={(e) => set(i, { months: e.target.value })} error={monthsError(i)} />
                      {editable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="sm:mt-8"
                          aria-label={`Remove the rule for ${types.find((t) => t.code === r.document_type)?.label ?? r.document_type.replace(/_/g, ' ')}`}
                          onClick={() => area.setDraft(rows.filter((_, j) => j !== i))}
                        >
                          <Trash2 size={15} aria-hidden />
                        </Button>
                      )}
                      {/* D22: the floor and the law that sets it, beside the rule it binds. */}
                      {floor ? (
                        <p className="text-muted-foreground text-xs sm:col-span-4">
                          {statuteText(floor)} — counted {anchorText(floor)}
                        </p>
                      ) : floorsUnread ? null : (
                        <p className="text-muted-foreground text-xs sm:col-span-4">At least 1 year after upload — the organisation’s minimum.</p>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            {editable && (
              <Button type="button" variant="outline" size="sm" className="mt-4" onClick={addRule}>
                <Plus size={15} aria-hidden />
                Add a rule
              </Button>
            )}
          </SettingsSection>

          <SettingsSection
            id="kept"
            icon={<Archive size={18} aria-hidden />}
            title="Kept now, by document type"
            description="What the service applies to each type in use today — the organisation’s rule where it has one, else the law’s minimum."
            footer={
              <p className="text-muted-foreground text-xs">
                An archived document past its date is removed by the daily retention job and leaves a record of what it was; a live one past its date is
                flagged and kept until someone decides. An administrator may keep one document longer, with a reason.
              </p>
            }
          >
            <KeptByType settings={s} byType={byType} />
          </SettingsSection>

          <EditBar
            dirty={area.dirty}
            saving={area.saving}
            onSave={save}
            onDiscard={area.discard}
            problem={area.problem}
            invalid={invalid ? 'Put right the rule marked above, then save.' : null}
            editable={editable}
          />
        </>
      )}
    </EditPage>
  )
}

/**
 * Each type in use with how long it is kept: the organisation's rule where it
 * has one, else the statute's floor, and always the floor and its citation
 * beside it (D22). An app's own rule for a type is listed under it.
 */
function KeptByType({ settings, byType }: { settings: SettingsAnswer; byType: Map<string, TypeFloor> }) {
  const types = settings.effective.document_types
  // The shipped floors are the law's minimum, said as such — never *the organisation's rule*.
  const rules = orgRetentionRules(settings)
  const used = types.filter((t) => t.enabled || rules.some((r) => r.document_type === t.code))
  return (
    <ul className="divide-border divide-y" aria-label="Retention by document type">
      {used.map((t) => {
        const floor = byType.get(t.code)
        const own = rules.find((r) => r.document_type === t.code && !r.owner_app)
        const forApps = rules.filter((r) => r.document_type === t.code && r.owner_app)
        const kept = Math.max(own?.months ?? 0, floor?.months ?? 0)
        return (
          <li key={t.code} className="grid gap-1 py-3 first:pt-0 last:pb-0 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4">
            <p className="text-foreground text-sm font-medium">{t.label}</p>
            <div className="min-w-0 space-y-1 text-sm">
              <p className="text-foreground">
                {kept ? `Kept ${monthsText(kept)} ${anchorText(floor)}` : 'Kept for the organisation’s minimum'}
                {own ? <span className="text-muted-foreground"> — the organisation’s rule</span> : floor ? <span className="text-muted-foreground"> — the law’s minimum</span> : null}
              </p>
              {floor && <p className="text-muted-foreground text-xs">{statuteText(floor)}</p>}
              {forApps.map((r) => (
                <p key={`${r.document_type}:${r.owner_app}`} className="text-muted-foreground text-xs">
                  Documents {r.owner_app} keeps: {monthsText(r.months)}
                </p>
              ))}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
